import { useCallback } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { Phone } from "./components/mobile/phone-shell";
import { alpha } from "./components/mobile/primitives";

// ─── Screen components ──────────────────────────────────────────────────────

import { JourneyWelcomeScreen } from "./sections/mobile-journey/journey-welcome";
import { JourneyPickSkillScreen } from "./sections/mobile-journey/journey-pick-skill";
import { JourneyVaultSetupScreen } from "./sections/mobile-journey/journey-vault-setup";
import { JourneyProcessingScreen } from "./sections/mobile-journey/journey-processing";
import { JourneyMarketplaceBrowseScreen } from "./sections/mobile-journey/journey-marketplace-browse";
import { JourneySkillDetailScreen } from "./sections/mobile-journey/journey-skill-detail";
import { JourneyInstallDataCheckScreen } from "./sections/mobile-journey/journey-install-data-check";

import { FlowFileDetail } from "./sections/mobile-flows/flow-file-detail";
import { FlowBiomarkerDetail } from "./sections/mobile-flows/flow-biomarker-detail";
import { FlowBiomarkerIndex } from "./sections/mobile-flows/flow-biomarker-index";
import {
  FlowHealthDataBrowser,
  FlowStepsDetail,
  FlowGraphExplorerDual,
} from "./sections/mobile-flows/flow-graph-explorer";

import { TodayScreen } from "./sections/mobile-screens/today-screen";
import { SkillsScreen } from "./sections/mobile-screens/skills-screen";
import { MeScreen } from "./sections/mobile-screens/me-screen";
import { InsightScreen } from "./sections/mobile-screens/insight-screen";
import { AnalysisRunScreen } from "./sections/mobile-screens/analysis-screen";
import { CaptureScreen } from "./sections/mobile-screens/capture-screen";

// ─── Constants ──────────────────────────────────────────────────────────────

const PHONE_SCALE = 0.48;
const FRAME_W = 270;
const FRAME_H = 570;
const NODE_W = Math.round(FRAME_W * PHONE_SCALE);
const NODE_H = Math.round(FRAME_H * PHONE_SCALE);

// ─── Area colors ────────────────────────────────────────────────────────────

const AREA_COLORS = {
  auth: {
    bg: "#E8E8ED",
    border: "var(--color-kin-olive-light)",
    text: "var(--color-kin-muted)",
    label: "Auth",
    accent: "var(--color-kin-muted)",
  },
  onboarding: {
    bg: "#E5F1E8",
    border: "var(--color-kin-green-soft)",
    text: "#3A5A44",
    label: "Onboarding",
    accent: "#34C759",
  },
  tabs: {
    bg: "#FFF7E6",
    border: "var(--color-kin-gold-dark)",
    text: "#8A7340",
    label: "Tabs",
    accent: "var(--color-kin-gold-dark)",
  },
  detail: {
    bg: "#E8F4F7",
    border: "var(--color-kin-teal)",
    text: "#4A7A85",
    label: "Detail Screens",
    accent: "var(--color-kin-teal)",
  },
  marketplace: {
    bg: "#F0EBF9",
    border: "var(--color-kin-lavender)",
    text: "#6B5BA0",
    label: "Marketplace",
    accent: "var(--color-kin-lavender)",
  },
  vault: {
    bg: "#FFF0E8",
    border: "#ff8a4d",
    text: "#B85A20",
    label: "Vault & Data",
    accent: "var(--color-kin-orange)",
  },
};

// ─── Screen → component mapping ─────────────────────────────────────────────

const SCREEN_CONFIG = {
  "sign-in": { Component: JourneyWelcomeScreen, hideTabBar: true },
  welcome: { Component: JourneyWelcomeScreen, hideTabBar: true },
  "pick-skill": { Component: JourneyPickSkillScreen, hideTabBar: true },
  "vault-setup": { Component: JourneyVaultSetupScreen, hideTabBar: true },
  processing: { Component: JourneyProcessingScreen, hideTabBar: true },
  home: { Component: TodayScreen, tab: "home" },
  skills: { Component: SkillsScreen, tab: "skills" },
  profile: { Component: MeScreen, tab: "profile", scrollable: true },
  connections: { Component: FlowHealthDataBrowser, tab: "profile", scrollable: true },
  "skill-detail": { Component: InsightScreen, tab: "skills" },
  "skill-processing": { Component: AnalysisRunScreen, hideTabBar: true },
  "metric-detail": { Component: FlowStepsDetail, hideTabBar: true },
  "file-detail": { Component: FlowFileDetail, tab: "profile" },
  "value-detail": { Component: FlowBiomarkerDetail, tab: "profile" },
  "vault-indexes": { Component: FlowBiomarkerIndex, tab: "profile" },
  "marketplace-browse": { Component: JourneyMarketplaceBrowseScreen, tab: "skills" },
  "marketplace-detail": { Component: JourneySkillDetailScreen, tab: "skills" },
  "install-data-check": { Component: JourneyInstallDataCheckScreen, tab: "skills" },
  "graph-explorer": { Component: FlowGraphExplorerDual, hideTabBar: true },
  capture: { Component: CaptureScreen, hideTabBar: true },
};

// ─── Node definitions ───────────────────────────────────────────────────────

const screenNodes = [
  // Auth
  { id: "sign-in", label: "Sign In", sub: "Google SSO", area: "auth" },

  // Onboarding
  { id: "welcome", label: "Welcome", sub: "First launch", area: "onboarding" },
  { id: "pick-skill", label: "Pick Skill", sub: "Choose starters", area: "onboarding" },
  { id: "vault-setup", label: "Vault Setup", sub: "Connect sources", area: "onboarding" },
  { id: "processing", label: "Processing", sub: "SSE step trace", area: "onboarding" },

  // Tabs
  { id: "home", label: "Home", sub: "Greeting, insights, trackers", area: "tabs", primary: true },
  { id: "skills", label: "Skills", sub: "Installed & dashboards", area: "tabs", primary: true },
  {
    id: "profile",
    label: "Profile",
    sub: "Profile, health data, library",
    area: "tabs",
    primary: true,
  },
  {
    id: "connections",
    label: "Health Data",
    sub: "Wearables, biomarkers, indexes",
    area: "vault",
  },

  // Detail screens
  { id: "skill-detail", label: "Skill Detail", sub: "Results, run, chat", area: "detail" },
  { id: "skill-processing", label: "Skill Processing", sub: "Live SSE trace", area: "detail" },
  { id: "metric-detail", label: "Metric Detail", sub: "Chart + trend", area: "detail" },

  // Vault & data
  { id: "file-detail", label: "File Detail", sub: "Metadata, values", area: "vault" },
  { id: "value-detail", label: "Value Detail", sub: "Biomarker + ref range", area: "vault" },
  { id: "vault-indexes", label: "Biomarker Index", sub: "Categories, flags", area: "vault" },
  { id: "capture", label: "Capture", sub: "Note, voice, photo, file", area: "vault" },

  // Marketplace
  { id: "marketplace-browse", label: "Marketplace", sub: "Search & discover", area: "marketplace" },
  {
    id: "marketplace-detail",
    label: "Skill Page",
    sub: "Description, install",
    area: "marketplace",
  },
  { id: "install-data-check", label: "Data Check", sub: "Readiness audit", area: "marketplace" },

  // Future
  {
    id: "graph-explorer",
    label: "Graph Explorer",
    sub: "Dual-axis compare",
    area: "detail",
  },
];

// ─── Layout positions ───────────────────────────────────────────────────────
// Organized as tiers flowing top → bottom. Nodes are ~130×274px at 0.48 scale.

const positions = {
  // Tier 0 — Entry
  "sign-in": { x: 0, y: 0 },

  // Tier 1 — Onboarding (left → right)
  welcome: { x: 0, y: 460 },
  "pick-skill": { x: 260, y: 460 },
  "vault-setup": { x: 520, y: 460 },
  processing: { x: 780, y: 460 },

  // Tier 2 — Main tabs
  home: { x: 0, y: 920 },
  skills: { x: 300, y: 920 },
  profile: { x: 600, y: 920 },

  // Tier 3 — First-level detail
  "marketplace-browse": { x: 80, y: 1380 },
  "skill-detail": { x: 340, y: 1380 },
  "file-detail": { x: 560, y: 1380 },
  "vault-indexes": { x: 780, y: 1380 },
  connections: { x: 1000, y: 1380 },

  // Tier 4 — Second-level detail
  "marketplace-detail": { x: 0, y: 1840 },
  "skill-processing": { x: 260, y: 1840 },
  "value-detail": { x: 640, y: 1840 },
  "metric-detail": { x: 1000, y: 1840 },

  // Tier 4b — Capture (from home FAB)
  capture: { x: -200, y: 1380 },

  // Tier 5 — Deepest
  "install-data-check": { x: 120, y: 2300 },
  "graph-explorer": { x: 1120, y: 2300 },
};

// ─── Edge definitions ───────────────────────────────────────────────────────

// Edge handles: "b"=bottom-source, "t"=top-target, "r"=right-source, "l"=left-target,
//               "ts"=top-source (for backward/upward edges), "bt"=bottom-target (same)

const navigationEdges = [
  // Auth → Onboarding/Home (vertical down)
  { source: "sign-in", target: "welcome", sH: "b", tH: "t", label: "new user" },
  { source: "sign-in", target: "home", sH: "b", tH: "t", label: "returning" },

  // Onboarding flow (horizontal, same row)
  { source: "welcome", target: "pick-skill", sH: "r", tH: "l" },
  { source: "pick-skill", target: "vault-setup", sH: "r", tH: "l" },
  { source: "vault-setup", target: "processing", sH: "r", tH: "l" },

  // Onboarding → Home (vertical down)
  { source: "pick-skill", target: "home", sH: "b", tH: "t", label: "skip" },
  { source: "processing", target: "home", sH: "b", tH: "t", label: "done" },

  // Home → details (vertical down)
  { source: "home", target: "skill-detail", sH: "b", tH: "t", label: "insight tap" },
  { source: "home", target: "file-detail", sH: "b", tH: "t", label: "file tap" },
  { source: "home", target: "capture", sH: "b", tH: "t", label: "FAB +" },

  // Skills tab → details (vertical down)
  { source: "skills", target: "skill-detail", sH: "b", tH: "t", label: "skill tap" },
  { source: "skills", target: "marketplace-browse", sH: "b", tH: "t", label: "explore" },
  { source: "skills", target: "install-data-check", sH: "b", tH: "t", label: "data gap" },

  // Skill detail → deeper (vertical down)
  { source: "skill-detail", target: "skill-processing", sH: "b", tH: "t", label: "run" },

  // Profile tab → details (vertical down)
  { source: "profile", target: "file-detail", sH: "b", tH: "t", label: "file tap" },
  { source: "profile", target: "vault-indexes", sH: "b", tH: "t", label: "indexes" },
  { source: "profile", target: "connections", sH: "b", tH: "t", label: "category tap" },

  // Health Data → detail screens (vertical down)
  { source: "connections", target: "metric-detail", sH: "b", tH: "t", label: "metric tap" },
  { source: "connections", target: "value-detail", sH: "b", tH: "t", label: "biomarker tap" },

  // File detail → deeper (vertical down)
  { source: "file-detail", target: "value-detail", sH: "b", tH: "t", label: "biomarker tap" },

  // Value detail → source (backward / upward)
  { source: "value-detail", target: "file-detail", sH: "ts", tH: "bt", label: "source file" },

  // Metric detail → Graph Explorer (vertical down)
  {
    source: "metric-detail",
    target: "graph-explorer",
    sH: "b",
    tH: "t",
    label: "compare",
  },

  // Marketplace flow (vertical down)
  {
    source: "marketplace-browse",
    target: "marketplace-detail",
    sH: "b",
    tH: "t",
    label: "skill tap",
  },
  {
    source: "marketplace-detail",
    target: "install-data-check",
    sH: "b",
    tH: "t",
    label: "install",
  },

  // Install → skill-detail (backward / upward)
  { source: "install-data-check", target: "skill-detail", sH: "ts", tH: "bt", label: "ready" },

  // Vault indexes → value (vertical down)
  { source: "vault-indexes", target: "value-detail", sH: "b", tH: "t", label: "marker tap" },
];

// ─── Build React Flow nodes & edges ─────────────────────────────────────────

function buildNodes() {
  return screenNodes.map((screen) => ({
    id: screen.id,
    position: positions[screen.id] || { x: 0, y: 0 },
    data: {
      label: screen.label,
      sub: screen.sub,
      area: AREA_COLORS[screen.area],
      primary: screen.primary,
      future: screen.future,
      screenId: screen.id,
    },
    type: "screenNode",
    draggable: true,
  }));
}

function buildEdges() {
  return navigationEdges.map((edge, i) => ({
    id: `e-${i}`,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sH,
    targetHandle: edge.tH,
    label: edge.label,
    type: "smoothstep",
    animated: edge.animated || false,
    style: { stroke: "var(--color-kin-muted-soft)", strokeWidth: 1.5 },
    labelStyle: {
      fontSize: 9,
      fontFamily: "'Manrope', system-ui",
      fontWeight: 600,
      fill: "var(--color-kin-muted)",
    },
    labelBgStyle: {
      fill: "rgba(247,244,237,0.92)",
      stroke: "rgba(0,0,0,0.06)",
      strokeWidth: 0.5,
      rx: 4,
    },
    labelBgPadding: [4, 6],
    markerEnd: { type: "arrowclosed", color: "var(--color-kin-muted-soft)", width: 14, height: 14 },
  }));
}

// ─── Handle styles (invisible for clean look) ──────────────────────────────

const handleStyle = {
  width: 6,
  height: 6,
  background: "transparent",
  border: "none",
};

// ─── Custom node: scaled Phone with actual screen ──────────────────────────

function ScreenNode({ data }) {
  const { label, sub, area, primary, future, screenId } = data;
  const config = SCREEN_CONFIG[screenId];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 8,
        opacity: future ? 0.7 : 1,
        position: "relative",
      }}
    >
      {/* Handles — positioned at phone frame edges, not full node edges */}
      {/* Top: incoming from above / outgoing upward (backward edges) */}
      <Handle id="t" type="target" position={Position.Top} style={handleStyle} />
      <Handle id="ts" type="source" position={Position.Top} style={handleStyle} />
      {/* Bottom: outgoing downward / incoming from below (backward edges) */}
      <Handle
        id="b"
        type="source"
        position={Position.Bottom}
        style={{ ...handleStyle, bottom: "auto", top: NODE_H }}
      />
      <Handle
        id="bt"
        type="target"
        position={Position.Bottom}
        style={{ ...handleStyle, bottom: "auto", top: NODE_H }}
      />
      {/* Left/Right: centered on phone frame */}
      <Handle
        id="l"
        type="target"
        position={Position.Left}
        style={{ ...handleStyle, top: NODE_H / 2 }}
      />
      <Handle
        id="r"
        type="source"
        position={Position.Right}
        style={{ ...handleStyle, top: NODE_H / 2 }}
      />

      {/* Future badge */}
      {future && (
        <div
          style={{
            position: "absolute",
            top: -8,
            right: -8,
            padding: "2px 8px",
            borderRadius: 6,
            background: area.border,
            fontSize: 8,
            fontWeight: 700,
            color: "#fff",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            zIndex: 2,
          }}
        >
          Future
        </div>
      )}

      {/* Scaled phone with actual screen design */}
      <div
        style={{
          width: NODE_W,
          height: NODE_H,
          position: "relative",
          filter: primary
            ? `drop-shadow(0 6px 20px ${alpha(area.accent, "30")})`
            : "drop-shadow(0 4px 12px rgba(0,0,0,0.12))",
        }}
      >
        <div
          style={{
            transform: `scale(${PHONE_SCALE})`,
            transformOrigin: "top left",
            width: FRAME_W,
            height: FRAME_H,
            pointerEvents: "none",
          }}
        >
          {config ? (
            <Phone
              active={config.tab || "home"}
              hideTabBar={config.hideTabBar}
              scrollable={config.scrollable || false}
            >
              <config.Component />
            </Phone>
          ) : (
            <Phone active="home" hideTabBar scrollable={false}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  padding: 24,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: area.accent,
                    opacity: 0.5,
                    textAlign: "center",
                    fontFamily: "'Manrope', system-ui",
                  }}
                >
                  {label}
                </span>
              </div>
            </Phone>
          )}
        </div>
      </div>

      {/* Label below phone */}
      <div style={{ textAlign: "center", maxWidth: NODE_W + 30 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: area.text,
            fontFamily: "'Manrope', system-ui",
            lineHeight: 1.2,
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 9,
            fontWeight: 400,
            color: alpha(area.text, "88"),
            fontFamily: "'Manrope', system-ui",
            lineHeight: 1.3,
            marginTop: 2,
          }}
        >
          {sub}
        </div>
      </div>
    </div>
  );
}

const nodeTypes = { screenNode: ScreenNode };

// ─── Legend ──────────────────────────────────────────────────────────────────

function Legend() {
  return (
    <div
      style={{
        position: "absolute",
        top: 16,
        left: 16,
        zIndex: 10,
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        padding: "10px 14px",
        borderRadius: 14,
        background: "rgba(247,244,237,0.92)",
        border: "1px solid rgba(255,255,255,0.50)",
        backdropFilter: "blur(16px)",
        boxShadow: "0 4px 16px -4px rgba(0,0,0,0.06)",
      }}
    >
      {Object.values(AREA_COLORS).map((a) => (
        <div key={a.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: 3,
              background: a.bg,
              border: `1.5px solid ${a.border}`,
            }}
          />
          <span
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: a.text,
              fontFamily: "'Manrope', system-ui",
            }}
          >
            {a.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Page component ─────────────────────────────────────────────────────────

export default function ScreenMapPage() {
  const [nodes, , onNodesChange] = useNodesState(buildNodes());
  const [edges, , onEdgesChange] = useEdgesState(buildEdges());

  const onInit = useCallback((instance) => {
    instance.fitView({ padding: 0.12 });
  }, []);

  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        fontFamily: "'Manrope', system-ui",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onInit={onInit}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.08}
        maxZoom={2.5}
        defaultEdgeOptions={{ type: "smoothstep" }}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="var(--color-kin-divider)" gap={24} size={1} />
        <Controls
          showInteractive={false}
          style={{
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 4px 12px -4px rgba(0,0,0,0.08)",
          }}
        />
        <MiniMap
          nodeColor={(node) => {
            const area = AREA_COLORS[screenNodes.find((s) => s.id === node.id)?.area];
            return area?.border || "#ccc";
          }}
          maskColor="rgba(247,244,237,0.8)"
          style={{
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 4px 12px -4px rgba(0,0,0,0.08)",
          }}
        />
      </ReactFlow>
      <Legend />
    </div>
  );
}
